import { watchEffect, onBeforeUnmount, unref, useHost, type Ref } from "vue";

type MaybeRef<T> = T | Ref<T>;
type MaybeGetter<T> = MaybeRef<T> | (() => T);

const resolve = <T>(v: MaybeGetter<T>): T => {
  return typeof v === "function" ? (v as () => T)() : unref(v);
};

/**
 * Applies an attribute to a host element and keeps it in sync.
 * If value is null/undefined/false -> removes the attribute.
 * If value is true -> sets attribute with empty string.
 *
 * @overload Without explicit host — uses the current custom element host via useHost().
 * @overload With explicit host — uses the provided element reference.
 */
// `function` retained: TS overload signatures are only supported on function declarations.
export function useHostAttribute(name: string, value: MaybeGetter<unknown>, opts?: { cleanup?: boolean }): void;
export function useHostAttribute(
  host: MaybeGetter<HTMLElement | null | undefined>,
  name: string,
  value: MaybeGetter<unknown>,
  opts?: { cleanup?: boolean },
): void;
export function useHostAttribute(
  hostOrName: MaybeGetter<HTMLElement | null | undefined> | string,
  nameOrValue: string | MaybeGetter<unknown>,
  valueOrOpts?: MaybeGetter<unknown> | { cleanup?: boolean },
  maybeOpts?: { cleanup?: boolean },
) {
  let host: MaybeGetter<HTMLElement | null | undefined>;
  let name: string;
  let value: MaybeGetter<unknown>;
  let opts: { cleanup?: boolean } | undefined;

  // Determine which overload was called
  if (typeof hostOrName === "string") {
    // useHostAttribute(name, value, opts?)
    host = useHost();
    name = hostOrName;
    value = nameOrValue;
    opts = valueOrOpts as { cleanup?: boolean } | undefined;
  } else {
    // useHostAttribute(host, name, value, opts?)
    host = hostOrName;
    name = nameOrValue as string;
    value = valueOrOpts;
    opts = maybeOpts;
  }

  const cleanup = opts?.cleanup ?? false;

  const stop = watchEffect(() => {
    const el = resolve(host);
    if (!el) return;

    const v = resolve(value);

    if (v === false || v == null) {
      el.removeAttribute(name);
      return;
    }

    if (v === true) {
      el.setAttribute(name, "");
    } else if (typeof v === "string") {
      el.setAttribute(name, v);
    } else if (typeof v === "number" || typeof v === "bigint") {
      el.setAttribute(name, v.toString());
    } else if (typeof v === "object") {
      el.setAttribute(name, JSON.stringify(v));
    }
  });

  onBeforeUnmount(() => {
    stop();
    if (cleanup) {
      const el = resolve(host);
      el?.removeAttribute(name);
    }
  });
}
